import React from 'react';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import dots_horizontal_rounded from './img/bx-dots-horizontal-rounded.svg';
import './ButtonActionDrop.scss';

interface ButtonActionDropProps {
  btns?: {
    btn: string;
    func: (id?: any) => void;
  }[];
  label?: string;
  size?: 's' | 'm' | '';
  color?: string;
  id?: number | string;
}

const ButtonActionDrop = ({
  btns = [],
  label = '',
  size = '',
  color = '',
  id = 1,
}: ButtonActionDropProps) => {
  const [isDrop, setIsDrop] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const ref = useRef<HTMLButtonElement>(null);
  const dropMenuRef = useRef<HTMLDivElement>(null);

  // Handler for clicks outside the element and scroll
  useEffect(() => {
    if (!isDrop) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const safeId = `button-action-drop-${id}`;
      if (!target.closest(`button-action-drop-${id}`)) {
        setIsDrop(false);
      }
    };

    const handleScroll = () => {
      setIsDrop(false);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isDrop]);

  // Измеряем высоту выпадающего меню после его рендера
  useLayoutEffect(() => {
    if (dropMenuRef.current && isDrop) {
      const height = dropMenuRef.current.offsetHeight;
      setDropdownHeight(height);
    }
  }, [isDrop, btns]);

  // Update dropdown position when opened
  useLayoutEffect(() => {
    if (isDrop && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      
      // Безопасные отступы от краев экрана
      const SAFE_MARGIN = 8;
      const DROPDOWN_WIDTH = 140;
      
      // Используем реальную высоту меню или оцениваем ее
      const menuHeight = dropdownHeight || Math.max(40 * btns.length, 80);
      
      let topPosition: number;
      
      // Проверяем, поместится ли меню снизу
      const spaceBelow = windowHeight - rect.bottom - SAFE_MARGIN;
      const spaceAbove = rect.top - SAFE_MARGIN;
      
      // Приоритет: показываем снизу, если хватает места
      if (spaceBelow >= menuHeight) {
        topPosition = rect.bottom;
      }
      // Если снизу не хватает, но сверху достаточно места
      else if (spaceAbove >= menuHeight) {
        topPosition = rect.top - menuHeight;
      }
      // Если не хватает места ни сверху, ни снизу
      else {
        // Выбираем сторону, где больше места
        if (spaceBelow >= spaceAbove) {
          // Размещаем снизу, даже если немного выйдет за край
          topPosition = rect.bottom;
        } else {
          // Размещаем сверху, но не выше безопасного отступа
          topPosition = Math.max(SAFE_MARGIN, rect.top - menuHeight);
        }
      }
      
      // Гарантируем, что меню не уйдет за верхний край
      topPosition = Math.max(SAFE_MARGIN, topPosition);
      
      // Горизонтальная позиция
      let leftPosition = rect.right - DROPDOWN_WIDTH;
      
      // Корректируем, если выходит за правый край
      if (leftPosition + DROPDOWN_WIDTH > windowWidth - SAFE_MARGIN) {
        leftPosition = windowWidth - DROPDOWN_WIDTH - SAFE_MARGIN;
      }
      
      // Корректируем, если выходит за левый край
      if (leftPosition < SAFE_MARGIN) {
        leftPosition = SAFE_MARGIN;
      }
      
      setPosition({
        top: topPosition,
        left: leftPosition,
      });
    }
  }, [isDrop, btns.length, dropdownHeight]);

  useEffect(() => {
    const dropdownOpen = (e: Event) => {
      const customEvent = e as CustomEvent;

      if (customEvent.detail.id !== id) setIsDrop(false);
    };

    document.addEventListener('dropdownOpen', dropdownOpen);
    return () => {
      document.removeEventListener('dropdownOpen', dropdownOpen);
    };
  }, [id]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const stateNext = !isDrop;
    setIsDrop(stateNext);

    if (stateNext) {
      document.dispatchEvent(
        new CustomEvent('dropdownOpen', { detail: { id } }),
      );
    }
  };

  const handleItemClick = (
    func: (id: number | string | React.MouseEvent) => void,
    e: React.MouseEvent,
  ) => {
    func(id);
    setIsDrop(false);
  };

  return (
    <div
      className="button-action-drop"
      id={`button-action-drop-${label + id}`}
      onClick={handleButtonClick}
    >
      <button
        ref={ref}
        style={{ background: color || '' }}
        className={`
                    button-action-drop__btn 
                    ${size === 'm' ? 'button-action-drop__btn-medium' : ''} 
                    ${size === 's' ? 'button-action-drop__btn-small' : ''}
                `}
      >
        <img src={dots_horizontal_rounded} alt="Actions" />
      </button>

      {isDrop && (
        <div
          ref={dropMenuRef}
          className="button-action-drop__drop-menu_active"
          style={{
            left: position.left,
            top: position.top,
            position: 'fixed',
            maxHeight: `calc(100vh - ${position.top + 20}px)`, // Ограничиваем максимальную высоту
          }}
        >
          <div className="button-action-drop__drop-menu-inside">
            {btns.map(({ btn, func }, index) => (
              <button
                key={index}
                className="button-action-drop__drop-menu__btn"
                onClick={(e) => handleItemClick(func, e)}
              >
                {btn}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { ButtonActionDrop };